import logger from '../../utils/logger-rum';
import React, {
	ReactElement,
	useEffect,
	useMemo, useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import Socket from '../../connection/socket';
import { GreenroomState as GS } from '../../stores/types/types';
import { ROOM_STATES } from '../../stores/reducers/settings';
import {
	IN_PROGRESS_STATUSES,
	BroadcastWarningTypes,
	ENDING_STATUSES,
	StreamStatus,
	BROADCAST_STATES,
	BroadcastStatus,
} from '../../stores/types/broadcasting';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import { RECORDING_OPTIONS } from './constants';
import useBroadcastState from './hooks/useBroadcastState';
import * as BroadcastActions from '../../stores/actions/broadcasting';
import * as SessionActions from '../../stores/actions/session';
import BroadcastModal from './broadcast-modal';
import LiveCounter from '../general-components/live-counter';
import useBroadcastAlerts from './hooks/useBroadcastAlerts';
import { showAlert } from '../general-components/alert-service';
import * as Alerts from '../general-components/alert-service';
import BroadcastDropdown from './broadcast-dropdown';
import { ReactComponent as DropdownLivestream } from '../../images/icons/dropdown-livestream.svg';
import { ReactComponent as DropdownRecord } from '../../images/icons/dropdown-record.svg';
import { ReactComponent as DropdownShowbuild } from '../../images/icons/dropdown-showbuild.svg';
import LoadingSpinner from '../general-components/loading-spinner';
import * as ModalActions from '../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../stores/reducers/modal';
import Portal from '../general-components/portal';
import {
	LiveStreamConfirmationModalProps,
	LiveStreamStart,
} from '../modal/templates/live-stream-confirmation';
import { withinUnsafeTimeLeft } from '../../utils/scheduled-streaming';
import { PopoverStatus } from '../general-components/popover';
import { GetBroadcastingErrorLogs } from '../../connection/broadcasting';
import usePrevious from '../../utils/use-previous';
import { setMetaMenuRoute, setMetaMenuState } from '../../stores/actions/tabs';
import { META_MENU_ROUTES, META_MENU_STATES } from '../../stores/types/tabs';
import colors from '../../utils/colors';
import { IconDotOutline, IconPlaySolid, IconRecordDuotone, IconRecordSolid, IconTellyFace } from '@brandlive/pixel';

export interface BroadcastWarningMessage {
	type: BroadcastWarningTypes;
	message: string;
	description: string;
}

interface BroadcastControlsProps {
	socket: Socket | null;
}

export interface BroadcastOption {
	value: string;
	dropdownTitle: string;
	dropdownSubtitle: string;
	dropdownIcon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> | ReactElement<any, any> ;
	dropdownLabel: string;
	number?: number;
	startIcon?: string;
	startIconColor?: string;
	startLabel?: string;
	progressLabel?: string
	endLabel?: string;
}

export const BROADCAST_OPTIONS = {
	STREAM: 'stream',
	RECORD: 'record',
	EDIT: 'showbuilder',
};

export const broadcastOptions = [
	{
		icon: ICONS.STREAM,
		label: 'Stream',
		value: BROADCAST_OPTIONS.STREAM,
		className: 'broadcast-option-stream',
		dropdownTitle: 'Stream',
		dropdownSubtitle: 'Take it live',
		dropdownIcon: <DropdownLivestream/>,
		dropdownIconPixel: <IconPlaySolid size={50} color={'var(--primitives-mode-1-gray-500)'}/>,
		dropdownLabel: 'Livestream',
		number: 0,
		startIcon: ICONS.STREAM_PLAY,
		startIconColor: COLORS.BLACK,
		startLabel: 'Stream',
		progressLabel: 'Streaming',
		endLabel: 'Stream',
	},
	{
		icon: ICONS.RECORD,
		label: 'Record only',
		shortLabel: 'Record',
		value: BROADCAST_OPTIONS.RECORD,
		className: 'broadcast-option-record',
		number: 1,
		dropdownTitle: 'Record',
		dropdownSubtitle: 'Create content',
		dropdownIcon: <DropdownRecord/>,
		dropdownIconPixel: <IconRecordSolid size={50}  color={'var(--primitives-mode-1-gray-500)'}/>,
		dropdownLabel: 'Recorder',
		startIcon: ICONS.STREAM_RECORD,
		startIconColor: COLORS.WHITE,
		startLabel: 'Record',
		progressLabel: 'Recording',
		endLabel: 'Recording',
	},
	{
		icon: ICONS.EDIT,
		label: 'Showbuilder',
		value: BROADCAST_OPTIONS.EDIT,
		className: 'broadcast-option-showbuilder',
		dropdownTitle: 'Showbuild',
		dropdownSubtitle: 'Plan the show',
		dropdownIcon: <DropdownShowbuild/>,
		dropdownIconPixel: <IconTellyFace size={48}  color={'var(--primitives-mode-1-gray-500)'}/>,
		dropdownLabel: 'Showbuilder',
	},
];

const broadcastWarningMessages: BroadcastWarningMessage[] = [
	{
		type: BroadcastWarningTypes.recordingStartFailure,
		message: 'Recording Failed',
		description:
			'Greenroom could not initiate your recording. Your stream is not otherwise affected.',
	},
];

const {
	STARTING_UP,
	BROADCAST_PREWARMING,
} = BROADCAST_STATES;

export default function BroadcastControls(props: BroadcastControlsProps) {
	const { socket } = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const backstageCommsActive = useSelector((s: GS) => s.SettingsReducer.backstageCommsActive);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const streamState = useSelector((s: GS) => s.BroadcastReducer.streamState);
	const serverState = useSelector((s: GS) => s.BroadcastReducer.serverState);
	const previousServerState = useSelector((s: GS) => s.BroadcastReducer.previousServerState);
	const goLiveFromModal = useSelector((s: GS) => s.BroadcastReducer.goLiveFromModal);
	const warningModalSent = useSelector((s: GS) => s.SessionReducer.warningModalSent);
	const broadcastWarnings = useSelector((s: GS) => s.BroadcastReducer.broadcastWarnings);
	const scheduledStream = useSelector((s: GS) => s.MeetingReducer.scheduledStream);
	const selectedRecordingOpt = useSelector((s: GS) => s.BroadcastReducer.selectedRecordingOption);
	const spielbergBroadcast = useSelector((s: GS) => s.BroadcastReducer.spielbergBroadcast);
	const broadcastStartedBy = useSelector((s: GS) => s.BroadcastReducer.broadcastStartedBy);
	const destinationStatus = useSelector((s: GS) => s.BroadcastReducer.destinationStatus);
	const { 
		use_spielberg: useSpielberg, 
		use_pixel: usePixel
	} = useSelector((s: GS) => s.SettingsReducer.featureFlags);

	const dispatch = useDispatch();
	const { handleTimeAlerts } = useBroadcastAlerts();
	const {
		startStopBroadcast,
		endActiveScheduledStream,
		canGoLive,
		broadcastStatus,
	} = useBroadcastState();

	const modeLineRef = useRef<HTMLDivElement>(null);

	const [mouseIsOver, setMouseIsOver] = useState(false);
	const [mockStreamStarting, setMockStreamStarting] = useState(false);
	const [mockStreamStopping, setMockStreamStopping] = useState(false);
	const [showBroadcastModal, setShowBroadcastModal] = useState(false);
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
	const [popoverStatus, setPopoverStatus] = useState<PopoverStatus>(PopoverStatus.Unset);

	useEffect(() => {
		setMockStreamStarting(false);
		if (
			broadcastStatus === BroadcastStatus.complete
			|| broadcastStatus === BroadcastStatus.aborted
			|| broadcastStatus === BroadcastStatus.failed
			|| broadcastStatus === BroadcastStatus.limitReached
		) {
			setMockStreamStopping(false);
		}
	}, [broadcastStatus]);

	const prevBroadcastStatus = usePrevious(broadcastStatus);
	useEffect(() => {
		if (
			!!prevBroadcastStatus
			&& broadcastStatus === BroadcastStatus.failed
			&& prevBroadcastStatus !== BroadcastStatus.failed
			&& broadcastStartedBy === user?.gr_user
			&& selectedRecordingOpt === RECORDING_OPTIONS.STREAM_AND_RECORD
		) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.BROADCAST_FAILED,
			}));
		}
	}, [prevBroadcastStatus, broadcastStatus]);

	const prevDestinationStatus = usePrevious(destinationStatus);
	useEffect(() => {
		if (
			!useSpielberg
			|| !prevDestinationStatus
			|| broadcastStartedBy !== user?.gr_user
			|| selectedRecordingOpt !== RECORDING_OPTIONS.STREAM_AND_RECORD
		) {
			return;
		}

		const { failed } = StreamStatus;
		const prevFailedDestinations = prevDestinationStatus.filter(({ status }) => status === failed);
		const failedDestinations = destinationStatus.filter(({ status }) => status === failed);
		const newFailedDestinations = failedDestinations.filter(
			({ id }) => !prevFailedDestinations?.find(({ id: prevId }) => id === prevId),
		);

		// only show for newly failing destinations
		if (newFailedDestinations.length) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.DESTINATION_FAILED,
			}));
		}
	}, [useSpielberg, prevDestinationStatus, destinationStatus]);

	// show the warnings
	useEffect(() => {
		if (broadcastWarnings && broadcastWarnings.length > 0) {
			for (let u = 0; u < broadcastWarnings.length; u += 1) {
				const broadcastWarning = broadcastWarnings[u];

				const warningMessage = broadcastWarningMessages.find(
					(warningMesssage) => warningMesssage.type === broadcastWarning,
				);

				if (!warningMessage) {
					// eslint-disable-next-line no-console
					logger.warn(
						`Tried to warning user about something but unable to find error message: ${JSON.stringify(
							broadcastWarning,
						)}`,
					);
				} else {
					const { message, description } = warningMessage;
					setTimeout(
						() => showAlert({
							message,
							description,
							onClick: () => { },
							duration: 6000,
							type: 'warning',
						}),
						1000,
					);
				}
			}
		}
	}, [broadcastWarnings]);

	const selectedBroadcastOption = useMemo(() => (
		roomState !== ROOM_STATES.EDITING
			? broadcastOptions[selectedRecordingOpt ?? 0]
			: broadcastOptions[2]
	), [selectedRecordingOpt, roomState]);

	const error = useMemo(() => serverState === (
		BROADCAST_STATES.BROADCAST_FAILED
		|| BROADCAST_STATES.BROADCAST_PREWARM_FAILED
		|| BROADCAST_STATES.KILLED
		|| BROADCAST_STATES.DRAINING
	), [serverState]);

	useEffect(() => {
		const ddLog = {
			family: 'BroadcastState',
			event: 'ChangeButtonState',
			location: 'broadcasting-controls.tsx',
			function: 'useMemo',
			serverState,
			previousServerState,
			streamState,
			host,
			hovering: mouseIsOver,
			meeting: meeting?.meeting,
			meeting_id: meeting?.meeting_id,
		};
		console.log('broadcast state 1', ddLog);

		if (serverState === BROADCAST_STATES.BROADCAST_FAILED) {
			const updatedDdLog = {
				...ddLog,
				function: `${ddLog.function}:failureSwitch`,
			};
			logger.error('Broadcast failed', updatedDdLog);

			if (meeting) GetBroadcastingErrorLogs(meeting.meeting);

			endActiveScheduledStream();
		}
	}, [serverState]);

	const secondCallback = (time: number) => {
		handleTimeAlerts(time);
	};

	const isLive = useMemo(
		() => broadcastStatus === BroadcastStatus.active && !mockStreamStopping,
		[broadcastStatus, mockStreamStopping],
	);

	const goingLive = useMemo(
		() => broadcastStatus === BroadcastStatus.starting || mockStreamStarting,
		[broadcastStatus, mockStreamStarting],
	);

	const isLiveOrGoingLive = useMemo(() => isLive || goingLive, [isLive, goingLive]);

	const isInitializing = useMemo(() => (
		useSpielberg ? false : (serverState === STARTING_UP || serverState === BROADCAST_PREWARMING)
	), [serverState, useSpielberg]);

	const isReady = useMemo(() => (
		useSpielberg
			? !spielbergBroadcast || !IN_PROGRESS_STATUSES.includes(spielbergBroadcast?.status)
			: canGoLive
	), [useSpielberg, spielbergBroadcast, canGoLive]);

	const getMain = useMemo(
		() => (
			isLive
				? (
					<>
						<div className="live-circle" />
						<span>{selectedBroadcastOption.progressLabel ?? ''}</span>
					</>
				) : (
					<>
						<span className="no-select">
							{selectedBroadcastOption.dropdownLabel ?? ''}
						</span>
						<Icon
							name={ICONS.CARROT_DOWN}
							color={
								selectedBroadcastOption.value === 'showbuilder'
									? COLORS.TEXT_GRAY_1200
									: COLORS.TEXT_GRAY_400
							}
							size={9.5}
						/>
					</>
				)
		),
		[isLive, selectedBroadcastOption, mouseIsOver],
	);

	function startBroadcast(clickedFromModal = false) {
		setShowBroadcastModal(false);

		// eslint-disable-next-line @typescript-eslint/no-use-before-define
		if (checkShowCommsOffWarningOrModal(clickedFromModal)) {
			return;
		}

		startStopBroadcast(
			goingLive,
			(value) => {
				setTimeout(() => {
					setMockStreamStarting(value);
				}, 250);
			},
		);

		if (!mockStreamStopping && (
			broadcastStatus === BroadcastStatus.active
			|| broadcastStatus === BroadcastStatus.starting
		)) {
			setMockStreamStopping(true);
		}
	}

	useEffect(() => {
		if (goLiveFromModal) {
			startBroadcast(false);
			dispatch(BroadcastActions.setGoLiveFromModal(false));
		}
	}, [goLiveFromModal]);

	const getButtonIcon = useMemo(() => {
		if (
			(broadcastStatus === BroadcastStatus.starting && !mouseIsOver)
			|| (isInitializing && selectedBroadcastOption.value === BROADCAST_OPTIONS.RECORD)
			|| (mockStreamStarting && !mouseIsOver)
		) {
			return (
				<LoadingSpinner
					height={10}
					width={10}
					color={usePixel
						? colors.blGrayscale1200
						: (selectedBroadcastOption.value === 'stream' ? colors.blGrayscale1200 : colors.blGrayscale100)
					}
					stroke={10}
					style={{ display: 'flex', margin: 'none' }}
				/>
			);
		}

		if (isLiveOrGoingLive && mouseIsOver) {
			return (
				<Icon
					name={ICONS.STREAM_STOP}
					color={usePixel ? COLORS.TEXT_GRAY_1200 : (selectedBroadcastOption.startIconColor ?? COLORS.BLACK)}
					size={12}
				/>
			);
		}

		if (
			(isLiveOrGoingLive && !mouseIsOver)
			|| !selectedBroadcastOption.startIcon
			|| !selectedBroadcastOption.startIconColor
		) {
			return <></>;
		}

		return (
			<Icon
				name={selectedBroadcastOption.startIcon}
				color={usePixel ? COLORS.TEXT_GRAY_1200 : selectedBroadcastOption.startIconColor}
				size={12}
			/>
		);
	}, [
		selectedBroadcastOption,
		broadcastStatus,
		mouseIsOver,
		isLiveOrGoingLive,
		isInitializing,
		mockStreamStopping,
		mockStreamStarting,
		spielbergBroadcast,
		usePixel,
	]);

	const getButtonLabel = useMemo(() => {
		if (!selectedBroadcastOption.startLabel) {
			return 'Showbuilder';
		}

		if (isLiveOrGoingLive && mouseIsOver) {
			return `End ${selectedBroadcastOption.endLabel}`;
		}

		if (isInitializing && selectedBroadcastOption.value === BROADCAST_OPTIONS.RECORD) {
			return 'Initializing';
		}

		switch (broadcastStatus) {
			case BroadcastStatus.active: {
				return mockStreamStopping
					? selectedBroadcastOption.startLabel
					: selectedBroadcastOption.progressLabel;
			}
			case BroadcastStatus.starting: {
				if (!mockStreamStopping && !mouseIsOver) {
					return 'Starting...';
				}
			} /* eslint-disable-next-line no-fallthrough */
			case BroadcastStatus.complete:
			case BroadcastStatus.ready: {
				if (mockStreamStarting && !mouseIsOver) {
					return 'Starting...';
				}
			} /* eslint-disable-next-line no-fallthrough */
			default: {
				return selectedBroadcastOption?.startLabel ?? '';
			}
		}
	}, [
		selectedBroadcastOption,
		isLiveOrGoingLive,
		mouseIsOver,
		isInitializing,
		broadcastStatus,
		mockStreamStarting,
		mockStreamStopping,
		spielbergBroadcast,
	]);

	const startTime = useMemo(() => {
		if (useSpielberg && spielbergBroadcast?.startTime) {
			return new Date(spielbergBroadcast?.startTime).getTime();
		}

		if (meeting && isLive) {
			return new Date(meeting.broadcast_live_start_time).getTime();
		}

		return null;
	}, [isLive, meeting, useSpielberg, spielbergBroadcast]);

	function checkShowCommsOffWarningOrModal(clickedFromModal: boolean) {
		if (
			(selectedRecordingOpt === RECORDING_OPTIONS.STREAM_AND_RECORD
				|| selectedRecordingOpt === RECORDING_OPTIONS.RECORD)
			&& warningModalSent
			&& !clickedFromModal
			&& !backstageCommsActive
			&& isReady
			&& !isLiveOrGoingLive
		) {
			Alerts.showAlert({
				message: 'Backstage Comms Off',
				description: 'Any backstage audio will be included in your recording.',
				onClick: () => { },
				duration: 4000,
			});
		}

		if (
			!backstageCommsActive
			&& !clickedFromModal
			&& !warningModalSent
			&& !isLiveOrGoingLive
		) {
			setShowBroadcastModal(true);
			dispatch(SessionActions.setWarningModalSent(true));
			return true;
		}

		return false;
	}

	function handleButtonMouseUp(clickedFromModal = false) {
		if (!host) return;
		if (scheduledStream?.startTime && withinUnsafeTimeLeft(scheduledStream.startTime)) {
			return;
		}

		if (
			selectedBroadcastOption.value === BROADCAST_OPTIONS.RECORD
			|| clickedFromModal
			|| (
				selectedBroadcastOption.value === BROADCAST_OPTIONS.STREAM
				&& isLiveOrGoingLive
			)
		) {
			startBroadcast(clickedFromModal);
		} else if (selectedBroadcastOption.value === BROADCAST_OPTIONS.STREAM) {
			dispatch(ModalActions.setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.LIVE_STREAM_CONFIRMATION,
				modalData: {
					socket,
					initialMode: scheduledStream?.startTime
						&& scheduledStream.startTime.valueOf() > Date.now()
						? LiveStreamStart.Schedule
						: LiveStreamStart.StartNow,
				} as LiveStreamConfirmationModalProps,
			}));
		}
	}

	const goToSettings = () => {
		dispatch(setMetaMenuRoute(META_MENU_ROUTES.EVENT_SETTINGS));
		dispatch(setMetaMenuState(META_MENU_STATES.OPENING));
		setShowBroadcastModal(false);
	};

	const modeLine = useMemo(() => (
		<Portal targetId="mode-line-portal">
			<SwitchTransition mode="in-out">
				<CSSTransition
					key={selectedBroadcastOption.value}
					nodeRef={modeLineRef}
					timeout={250}
					classNames="fade"
					addEndListener={(done) => {
						modeLineRef.current?.addEventListener('transitionend', done, false);
					}}
					unmountOnExit
				>
					<div
						className={classNames(
							'header-mode-line',
							selectedBroadcastOption.value,
						)}
						ref={modeLineRef}
					/>
				</CSSTransition>
			</SwitchTransition>
		</Portal>
	), [selectedBroadcastOption]);

	if (!host && broadcastStatus !== BroadcastStatus.active) {
		return modeLine;
	}

	return (
		<>
			<div
				className={classNames(
					`broadcast-controls-container ${selectedBroadcastOption.value} stylepacks-do-fade`,
					{ 'full-button': goingLive || (isLive && mouseIsOver) },
					{ 'is-live': isLive },
					{ error },
					{ 'dropdown-open': popoverStatus === PopoverStatus.Open },
				)}
				onMouseEnter={() => setMouseIsOver(host)}
				onMouseLeave={() => setMouseIsOver(false)}
				style={{ pointerEvents: !host ? 'none' : 'auto' }}
				data-testid='broadcast-controls-container'
			>
				{modeLine}
				<div
					className='broadcast-controls-main'
					onMouseDown={() => {
						if (
							broadcastStatus === BroadcastStatus.starting
							|| broadcastStatus === BroadcastStatus.active
							|| mockStreamStarting
							|| mockStreamStopping
						) {
							return;
						}
						setDropdownIsOpen(true);
					}}
					data-testid='broadcast-controls-main'
				>
					{getMain}
				</div>
				{selectedBroadcastOption.value !== 'showbuilder' && (
					<div
						className={classNames(
							'broadcast-controls-button',
							{
								initializing: isInitializing
								&& selectedBroadcastOption.value === BROADCAST_OPTIONS.RECORD,
							},
							{ ending: mockStreamStopping || ENDING_STATUSES.includes(broadcastStatus) },
						)}
						onMouseUp={() => selectedBroadcastOption.value !== 'showbuilder' && handleButtonMouseUp(false)}
						data-testid='broadcast-controls-button'
					>
						{getButtonIcon}
						{(!isLive || mouseIsOver) && (
							<span>{getButtonLabel}</span>
						)}
						{isLive && (
							<LiveCounter
								notLive={false}
								isHeader={true}
								isHost={host}
								startTime={startTime}
								secondCallback={secondCallback}
							/>
						)}
					</div>
				)}
				<BroadcastDropdown
					socket={socket}
					roomState={roomState}
					selectedBroadcastOption={selectedBroadcastOption}
					isOpen={dropdownIsOpen}
					onClose={() => setDropdownIsOpen(false)}
					setPopoverStatus={setPopoverStatus}
					usePixel={usePixel}
				/>
			</div>
			{showBroadcastModal && (
				<BroadcastModal
					broadcastOption={selectedBroadcastOption.value}
					goToSettings={goToSettings}
					handleClick={(opt: boolean) => {
						setMouseIsOver(false);
						handleButtonMouseUp(opt);
					}}
					icon={getButtonIcon}
					text={selectedBroadcastOption.startLabel ?? ''}
				/>
			)}
		</>
	);
}
